import React from "react";
import { listContenidoItems } from "../utils/contenidoOrden";
import { displayArchivoNombre } from "../utils/archivoDisplayName";
import { getMessage } from "../data/locales";
import CourseDocumentFileActions from "./CourseDocumentFileActions";
import "./TemarioContenidoPreview.css";

/**
 * Read-only hierarchical syllabus: tab content → nodes → files and tests (recursive).
 * @param {Array<{ nombre: string, archivos: Array<{ nombre: string, url?: string, storageKey?: string }>, hijos?: Array<{ nombre: string }> }>} props.nodes
 * @param {(archivo: { nombre: string, url?: string, storageKey?: string }) => void} [props.onArchivoClick]
 * @param {(archivo: { nombre: string, url?: string, storageKey?: string }) => void} [props.onArchivoDownload]
 * @param {boolean} [props.linkArchivos]
 */
export default function TemarioContenidoPreview({ nodes, onArchivoClick, onArchivoDownload, linkArchivos }) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return null;
  }
  return (
    <ul className="temario-preview-root" style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
      {nodes.map((node, idx) => (
        <TemarioPreviewNode
          key={`${idx}-${node?.nombre || "bloque"}`}
          node={node}
          depth={0}
          onArchivoClick={onArchivoClick}
          onArchivoDownload={onArchivoDownload}
          linkArchivos={linkArchivos}
        />
      ))}
    </ul>
  );
}

function TemarioPreviewNode({ node, depth, onArchivoClick, onArchivoDownload, linkArchivos }) {
  if (!node || typeof node !== "object") {
    return null;
  }
  const hijos = Array.isArray(node.hijos) ? node.hijos : [];
  const items = listContenidoItems(node);
  const pad = depth > 0 ? Math.min(depth * 16, 64) : 0;
  const hasFileActions =
    typeof onArchivoClick === "function" || typeof onArchivoDownload === "function";
  return (
    <li style={{ marginBottom: 10, paddingLeft: pad }}>
      <strong>{node.nombre || "—"}</strong>
      {items.length > 0 && (
        <ul style={{ marginTop: 4, marginBottom: hijos.length ? 8 : 0 }}>
          {items.map((item) => {
            if (item.kind === "test") {
              const titulo = typeof item.testRef?.titulo === "string" ? item.testRef.titulo : "";
              return (
                <li key={`test-${item.sourceIndex}`} className="temario-preview-test-ref">
                  {getMessage("topic.testResourceLabel", { title: titulo })}
                </li>
              );
            }
            const fileLabel = displayArchivoNombre(item.archivo);
            return (
              <li key={`file-${item.sourceIndex}`} className="temario-preview-file-row">
                {hasFileActions ? (
                  <>
                    <span className="temario-preview-file-plain">{fileLabel}</span>
                    <span className="temario-preview-file-actions">
                      <CourseDocumentFileActions
                        archivo={item.archivo}
                        onOpen={onArchivoClick}
                        onDownload={onArchivoDownload}
                      />
                    </span>
                  </>
                ) : (
                  <span className={linkArchivos ? "temario-preview-file-plain" : undefined}>
                    {fileLabel}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {hijos.length > 0 && (
        <ul style={{ listStyle: depth === 0 ? "none" : "disc", paddingLeft: depth === 0 ? 0 : 20, margin: 0 }}>
          {hijos.map((child, cidx) => (
            <TemarioPreviewNode
              key={`${cidx}-${child.nombre ?? ""}`}
              node={child}
              depth={depth + 1}
              onArchivoClick={onArchivoClick}
              onArchivoDownload={onArchivoDownload}
              linkArchivos={linkArchivos}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
